<template>
  <div class="laundry-form">
    <validation-observer
      ref="formValidation"
    >
      <!-- Laundry Location Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.address')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.laundryLocation')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-textarea
            v-model="formData.address"
            :rows="2"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterAddress')"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Laundry Service Description -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.description')"
        rules=""
      >
        <b-form-group
          :label="$t('field.laundryServiceDescription')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-textarea
            v-model="formData.description"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterDescription')"
            maxlength="300"
          />
          <div class="d-flex justify-content-between align-items-center mt-1">
            <small class="text-muted">{{ formData.description.length }}/300</small>
            <small class="text-danger">{{ errors[0] }}</small>
          </div>

          <!-- File Upload Section -->
          <div class="media-section mt-2">
            <div
              :style="{
                paddingBottom: 'calc(0.438rem + 1px)'
              }"
            >
              <span>
                {{ $t('field.attachments') }} <small class="text-muted">({{ $t('field.optional') }})</small>
              </span>
            </div>
            <div class="media-grid">
              <!-- Photo Selection -->
              <file-selection-card
                type="photo"
                :title="$t('field.photo')"
                :file="getFileByType('photo')"
                @select="openFileModal('photo')"
                @remove="removeFileByType('photo')"
              />

              <!-- Video Selection -->
              <file-selection-card
                type="video"
                :title="$t('field.video')"
                :file="getFileByType('video')"
                @select="openFileModal('video')"
                @remove="removeFileByType('video')"
              />

              <!-- Voice Selection -->
              <file-selection-card
                type="voice"
                :title="$t('field.voice')"
                :file="getFileByType('voice')"
                @select="openFileModal('voice')"
                @remove="removeFileByType('voice')"
              />
            </div>
          </div>

          <!-- Charge Type Section -->
          <div class="charge-type-section mt-2">
            <div
              :style="{
                paddingBottom: 'calc(0.438rem + 1px)'
              }"
            >
              <span>
                {{ $t('field.chargeType') }}
              </span>
            </div>
            <b-form-radio-group
              v-model="formData.chargeType"
              :options="[
                { text: $t('field.singlePayment'), value: 'single' }
              ]"
              :state="errors.length === 0 ? null : false"
            />
          </div>
        </b-form-group>
      </validation-provider>

      <!-- Service Time Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.scheduleType')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.serviceTime')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-radio-group
            v-model="formData.scheduleType"
            :options="[
              { text: $t('field.immediately'), value: 1 },
              { text: $t('field.customTime'), value: 2 }
            ]"
            :state="errors.length === 0 ? null : false"
          />
          <n-date-time-picker
            v-if="formData.scheduleType === 2"
            v-model="formData.scheduleDate"
            :state="errors.length === 0 ? null : false"
            :disable-before-today="true"
            :disable-after-today="false"
            class="mt-2"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>
    </validation-observer>

    <!-- File Selection Modal -->
    <file-selection-modal
      v-model="isFileModalVisible"
      :file-type="currentFileType"
      :files="filteredFiles"
      :current-selected-file="getFileByType(currentFileType)"
      @select="confirmFileSelection"
    />
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormRadioGroup,
  BFormTextarea,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NDateTimePicker from '@/components/NDateTimePicker.vue'
import FileSelectionModal from '@/components/FileSelectionModal.vue'
import FileSelectionCard from '@/components/FileSelectionCard.vue'
import RepositoryFactory from '@/repositories/RepositoryFactory'

const ChatMessageRepository = RepositoryFactory.get('chatMessage')

export default {
  name: 'LaundryForm',

  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    NDateTimePicker,
    FileSelectionModal,
    FileSelectionCard,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    chatChannelId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      formData: {
        scheduleType: 1,
        scheduleDate: '',
        files: [],
        addressType: 0,
        address: '',
        subscriptionType: 0,
        subscriptionPlanId: null,
        description: '',
        chargeType: 'single',
      },
      isFileModalVisible: false,
      currentFileType: null,
      chatFiles: [],
      filteredFiles: [],
    }
  },

  watch: {
    value: {
      handler(newValue) {
        if (newValue && JSON.stringify(newValue) !== JSON.stringify(this.formData)) {
          this.formData = {
            scheduleType: newValue.scheduleType ?? 1,
            scheduleDate: newValue.scheduleDate ?? '',
            files: newValue.files ?? [],
            addressType: newValue.addressType ?? 0,
            address: newValue.address ?? '',
            subscriptionType: newValue.subscriptionType ?? 0,
            subscriptionPlanId: newValue.subscriptionPlanId ?? null,
            description: newValue.description ?? '',
            chargeType: newValue.chargeType ?? 'single',
          }
        }
      },
      immediate: true,
      deep: true,
    },
    formData: {
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
          // Create a sanitized copy with only essential file properties
          const sanitizedValue = { ...newValue }

          if (sanitizedValue.files && sanitizedValue.files.length) {
            sanitizedValue.files = sanitizedValue.files.map(file => ({
              originalName: file.originalName,
              type: file.type,
              mimeType: file.mimeType,
              url: file.url,
            }))
          }

          this.$emit('input', sanitizedValue)
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getAttachments()
  },
  methods: {
    async validate() {
      if (this.$refs.formValidation) {
        const isValid = await this.$refs.formValidation.validate()

        if (isValid) {
          // Ensure only essential file properties are sent to the server
          const sanitizedFormData = { ...this.formData }

          if (sanitizedFormData.files && sanitizedFormData.files.length) {
            sanitizedFormData.files = sanitizedFormData.files.map(file => ({
              originalName: file.originalName,
              type: file.type,
              mimeType: file.mimeType,
              url: file.url,
            }))
          }

          // Replace the formData with the sanitized version
          this.formData = sanitizedFormData
        }

        return isValid
      }
      return false
    },

    reset() {
      this.formData = {
        scheduleType: 1,
        scheduleDate: null,
        files: [],
        addressType: 0,
        address: '',
        subscriptionType: 0,
        subscriptionPlanId: null,
        description: '',
        chargeType: 'single',
      }
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },

    openFileModal(type) {
      this.currentFileType = type
      // Filter files by type only
      this.filteredFiles = this.chatFiles.filter(file => file.type === this.currentFileType)
      this.isFileModalVisible = true
    },

    confirmFileSelection(selectedFile) {
      if (!selectedFile || !this.currentFileType) {
        return
      }

      // Remove any existing file of this type
      this.removeFileByType(this.currentFileType)

      // Add the selected file with all necessary properties for preview
      this.formData.files.push({
        originalName: selectedFile.name,
        type: this.currentFileType,
        mimeType: selectedFile.mimeType,
        url: selectedFile.url,
      })
    },

    hasFileType(type) {
      return this.formData.files.some(file => file.type === type)
    },

    getFileByType(type) {
      return this.formData.files.find(file => file.type === type) || null
    },

    removeFileByType(type) {
      const index = this.formData.files.findIndex(file => file.type === type)
      if (index !== -1) {
        this.formData.files.splice(index, 1)
      }
    },
    getAttachments() {
      ChatMessageRepository.getAttachments(this.chatChannelId).then(response => {
        // Handle different response structures
        const attachments = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || [])

        // Filter only image, video, and voice file types
        const filteredAttachments = attachments.filter(attachment => {
          const mediaType = attachment.mediaType || ''
          return mediaType.startsWith('image/')
                 || mediaType.startsWith('video/')
                 || mediaType.startsWith('audio/')
        })

        // Map API response data to the format expected by the component
        // Only include essential properties: mimeType, url, type, and originalName
        this.chatFiles = filteredAttachments.map(attachment => {
          // Determine file type based on mediaType
          let type = 'photo' // Default to photo
          if (attachment.mediaType) {
            if (attachment.mediaType.startsWith('video/')) {
              type = 'video'
            } else if (attachment.mediaType.startsWith('audio/')) {
              type = 'voice'
            }
          }

          // Only include essential properties for the file selection
          return {
            id: attachment.id, // Keep id for internal reference only
            name: attachment.message || `file-${attachment.id}`,
            mimeType: attachment.mediaType || '',
            date: new Date(attachment.createdAt), // Keep date for display purposes
            type,
            url: attachment.mediaUrl || '',
          }
        })

        console.log('chatFiles', this.chatFiles)
      }).catch(error => {
        console.error('Error fetching attachments:', error)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.laundry-form {
  .form-group:last-child {
    margin-bottom: 0;
  }

  .media-section {
    h6 {
      font-weight: 600;
      color: #495057;
    }

    .media-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 1rem;

      @media (max-width: 576px) {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
